import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Company, FeatureUser, USER_ID_KEY } from '@tacliatech/types';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import {
  filter,
  map,
  take,
  catchError,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { StorageService } from '../storage';
import { PermissionService } from '../permissions';
import { Router } from '@angular/router';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { oldModuleStructure } from './company-module.const';
import { FeatureType, FeatureTypes } from '@web-frontend/shared/enums/module';
import { AuthService } from '../auth';
import { ModulesService } from '../modules/modules.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyModuleService {
  private modulesStorage = new BehaviorSubject<Company.Module[]>(null);
  private subscriptions = new Subscription();

  modules$ = this.modulesStorage.asObservable();

  activeModules$ = this.modules$.pipe(
    map((res) => (res === null ? null : res.filter((el) => el.active)))
  );

  idActiveModules$ = this.activeModules$.pipe(
    map((res) => res?.map((el) => el.id) || [])
  );

  private restrictedDefaultModules = ['ASSETS', 'EQUIPMENTS'];
  restrictedModules: Array<string> = this.restrictedDefaultModules;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private permissionService: PermissionService,
    private router: Router,
    private featureFlagService: FeatureFlagsService,
    private modulesService: ModulesService
  ) {}

  get idActiveModules(): Company.IdModule[] {
    if (!this.modulesStorage.value) return [];

    return this.modulesStorage.value
      .filter((module) => module.active)
      .map((module) => module.id);
  }

  private async setRestrictedModules(): Promise<void> {
    const companyCreatedAt =
      new Date(JSON.parse(StorageService.companyData)?.created_at) ??
      new Date();
    return this.featureFlagService
      .getValue('restricted_modules', '[]', {
        companyCreatedAt: companyCreatedAt.getTime(),
      })
      .then((value) => {
        if (typeof value === 'string') {
          try {
            this.restrictedModules = JSON.parse(value.replace(/'/g, '"'));
          } catch (e) {
            this.restrictedModules = this.restrictedDefaultModules;
          }
        }
      });
  }

  private calculateCanAccess(module: Company.Module): boolean {
    if (this.restrictedModules.includes(module.id)) {
      return false;
    }
    const permissions = Company.RoutesPermissionsMap[module.id];
    if (permissions) {
      const canAccess = permissions.filter((p) =>
        this.permissionService.permissions.includes(p.feature)
      );
      // Don't check module.active here - that creates circular dependency
      return canAccess.length > 0;
    }
    return false;
  }

  private calculateNewModuleCanAccess(
    module: Company.Module,
    enabledFeatures: FeatureType[]
  ): boolean {
    const newModuleMap = {
      [Company.IdModule.ClockHours]: [
        FeatureTypes.CLOCK_HOURS,
        FeatureTypes.ABSENCES,
      ],
      [Company.IdModule.Finals]: [FeatureTypes.CONTACTS],
      [Company.IdModule.Sales]: [
        FeatureTypes.INVOICES,
        FeatureTypes.BUDGETS,
        FeatureTypes.WAYBILLS,
        FeatureTypes.PROFORMAS,
        FeatureTypes.SALES_TICKETS,
        FeatureTypes.CATALOGUE,
      ],
      [Company.IdModule.Expenses]: [FeatureTypes.EXPENSES],
      [Company.IdModule.Deals]: [
        FeatureTypes.DEALS,
        FeatureTypes.ONLINE_BOOKING,
      ],
      [Company.IdModule.Projects]: [FeatureTypes.PROJECTS],
      [Company.IdModule.Activities]: [FeatureTypes.INTERNAL_TASKS],
      [Company.IdModule.Payments]: [FeatureTypes.PAYMENTS],

      [Company.IdModule.InternalVendors]: [FeatureTypes.CONTACTS],
      [Company.IdModule.Rates]: [FeatureTypes.CATALOGUE],
      [Company.IdModule.Reports]: [FeatureTypes.REPORTS],
      [Company.IdModule.Users]: [
        FeatureTypes.CLOCK_HOURS,
        FeatureTypes.ABSENCES,
        FeatureTypes.DOCUMENT_MANAGEMENT,
      ],
      [Company.IdModule.Equipments]: [FeatureTypes.EQUIPMENT],
      [Company.IdModule.Assets]: [true], // deprecated
    };
    return enabledFeatures.some(
      (feature) =>
        newModuleMap[module.id]?.includes(feature) ||
        newModuleMap[module.id]?.includes(true)
    );
  }

  private updateModules(
    modules: Company.Module[],
    features: FeatureType[]
  ): void {
    modules.forEach((mod) => {
      const canAccessByFeature = this.calculateNewModuleCanAccess(
        mod,
        features
      );
      const canAccessByPermission = this.calculateCanAccess(mod);

      // Module is active only if BOTH feature AND permission checks pass
      mod.active = canAccessByFeature && canAccessByPermission;
      mod.canAccess = mod.active;
    });

    this.updateUser({ ...this.authService.user?.company, modules });
    this.modulesStorage.next(modules);
  }

  async setAccessibleModules(): Promise<void> {
    const features = await this.modulesService.enabledFeatures$
      .pipe(
        filter((features) => features !== null),
        take(1)
      )
      .toPromise();

    const modules = oldModuleStructure;
    this.updateModules(modules, features);
  }

  async init(): Promise<void> {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();

    await this.setRestrictedModules(); // to be deprecated
    await this.setAccessibleModules();
  }

  goFirstPermissionActive(activesModules: Company.Module[]): void {
    let goTo = '';
    activesModules.forEach((activeModule) => {
      const match = Company.RoutesPermissionsMap[
        activeModule.id
      ]?.find((route) =>
        this.permissionService.permissions.includes(route.feature)
      )?.url;
      if (match && !goTo) {
        goTo = match;
      }
    });

    if (!goTo && this.hasModuleManagementAccess()) {
      goTo = 'admin/module-management';
    }

    this.router.navigateByUrl(
      goTo ||
        `admin/profile?view=user&id=${StorageService.GetItem(USER_ID_KEY)}`
    );
  }

  private hasModuleManagementAccess(): boolean {
    return this.permissionService.hasPermission(
      FeatureUser.Functionalitie.show
    );
  }

  goFirstRouteActive(): void {
    this.activeModules$
      .pipe(
        filter((modules) => modules !== null), // Wait until initialized
        debounceTime(100),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        take(1),
        map((modules) => modules.sort((a, b) => a.index - b.index)),
        catchError(() => of([]))
      )
      .subscribe((activesModules) => {
        this.goFirstPermissionActive(activesModules);
      });
  }

  activateCompanyFeature(module: string) {
    return this.http.post(`:API_BASE_URL/company/features/${module}`, null);
  }

  updateUser = (res: Company.Output): void => {
    const user = { ...this.authService.user, company: res };
    this.authService.updateUserCache = user;
  };

  clearModuleStorage(): void {
    this.modulesStorage.next(null);
  }
}
